import { useSelector } from "react-redux";
// REDUX
import type { RootState } from "../app/store";
// UTILS
import {
  FiringSurface,
  effectiveFiringMode,
  firesOnFloorClick,
  resolveConfiguredMode,
} from "../utils/actionFiringMode";
import { ActionFiringMode } from "../flatbuffers/generated/archive/action-firing-mode";

/**
 * What the logged-in operator has configured, across both levels.
 *
 * The ONE place a component reads the setting. Reading
 * `userLogged.actionFiringMode` directly is the mistake this exists to prevent:
 * on its own it is usually INHERIT, which says nothing about what the operator
 * will actually get — the organization's value is the other half, and the two
 * only mean something combined.
 *
 * Returns the CONFIGURED mode, which may be INHERIT when neither level has
 * chosen. That is deliberately not resolved to a behaviour here: the two
 * dispatching surfaces have different long-standing defaults, so the surface
 * applies its own. See `useFiresOnFloorClick`, which is what call sites want.
 */
export function useConfiguredActionFiringMode(): ActionFiringMode {
  // Already resolved across both levels by the organization load path, which
  // runs once and writes the answer here. Read FIRST so the Car Calls pad and
  // an Actions card cannot end up disagreeing on the same deployment: two
  // readers deriving the same value from two different places is how they
  // start.
  const fromSlice = useSelector((state: RootState) => state.monitoring.actionFiringMode);

  // The fallback, for the window before that load has run — and for any surface
  // reachable without it. Same resolver, so this cannot mean something
  // different from the value above; it is the same answer computed earlier.
  //
  // Both spellings are read because the two endpoints that carry this field do
  // not agree on case, and a user whose own override is dropped silently
  // inherits the organization's setting instead of getting their own.
  const userLogged = useSelector((state: RootState) => state.home.userLogged);

  // Compared explicitly rather than tested for truthiness. INHERIT is ordinal
  // 0, so `if (fromSlice)` would read "nobody chose" and "not loaded yet" as
  // the same thing purely by accident of enum ordering — and would start
  // silently ignoring a real setting the moment a member is added ahead of it.
  if (fromSlice !== undefined && fromSlice !== ActionFiringMode.INHERIT) return fromSlice;

  return resolveConfiguredMode(
    userLogged?.actionFiringMode ??
      (userLogged as { action_firing_mode?: string })?.action_firing_mode,
    userLogged?.org?.actionFiringMode ??
      (userLogged?.org as { action_firing_mode?: string })?.action_firing_mode,
  );
}

/**
 * The mode a given surface will actually behave as, INHERIT resolved away.
 */
export function useEffectiveActionFiringMode(surface: FiringSurface): ActionFiringMode {
  return effectiveFiringMode(useConfiguredActionFiringMode(), surface);
}

/**
 * May a floor click, on this surface, itself be the act that sends?
 *
 * The question every dispatching surface actually has. Note what it does NOT
 * answer: whether this particular BUTTON TYPE can be collapsed to one act.
 * That is `floorSelectionCanSend`, and both must agree before a tap sends —
 * the mode is the operator's preference, the protocol is the type's own
 * requirement, and a preference cannot waive a requirement.
 */
export function useFiresOnFloorClick(surface: FiringSurface): boolean {
  return firesOnFloorClick(useConfiguredActionFiringMode(), surface);
}
